import { useState, useCallback, useEffect, useMemo } from 'react'
import {
    Modal,
    ModalDialog,
    ModalClose,
    Typography,
    Divider,
    Stack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Chip,
    Alert,
    IconButton,
    Sheet,
    Switch,
} from '@mui/joy'
import CloseIcon from '@mui/icons-material/Close'

import { createGroup, addAccountToGroup } from '../../../api/telegramApi'
import { type TelegramAccount } from '../types'

interface AddGroupModalProps {
    open: boolean
    accounts: TelegramAccount[]
    onClose: () => void
    onCreate: () => void
}

export function AddGroupModal({ open, accounts, onClose, onCreate }: AddGroupModalProps): JSX.Element {
    const [peerId, setPeerId] = useState('')
    const [originalName, setOriginalName] = useState('')
    const [brokerCompanyName, setBrokerCompanyName] = useState('')
    const [joinLink, setJoinLink] = useState('')
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
    const [creating, setCreating] = useState(false)
    const [modalMessage, setModalMessage] = useState<string | null>(null)
    const [modalError, setModalError] = useState<string | null>(null)

    const activeAccounts = useMemo(() => accounts.filter((a) => a.status === 'active'), [accounts])

    const resetState = useCallback(() => {
        setPeerId('')
        setOriginalName('')
        setBrokerCompanyName('')
        setJoinLink('')
        setSelectedAccounts([])
        setCreating(false)
        setModalMessage(null)
        setModalError(null)
    }, [])

    useEffect(() => {
        if (!open) {
            resetState()
        }
    }, [open, resetState])

    const handleCreate = useCallback(async () => {
        if (!peerId.trim()) {
            setModalError('Peer ID is required')
            return
        }
        setCreating(true)
        setModalMessage(null)
        setModalError(null)
        try {
            await createGroup({
                peerId: peerId.trim(),
                originalName: originalName.trim() || null,
                brokerCompanyName: brokerCompanyName.trim() || null,
                joinLink: joinLink.trim() || null,
            })

            for (const phone of selectedAccounts) {
                try {
                    await addAccountToGroup(peerId.trim(), phone)
                } catch (e) {
                    console.warn(`Failed to add account ${phone} to group:`, e)
                }
            }

            setModalMessage(`Group created successfully${selectedAccounts.length > 0 ? ` with ${selectedAccounts.length} watching account(s)` : ''}`)
            onCreate()

            setTimeout(() => {
                onClose()
            }, 1000)
        } catch (e) {
            setModalError((e as Error)?.message || 'Failed to create group')
        } finally {
            setCreating(false)
        }
    }, [peerId, originalName, brokerCompanyName, joinLink, selectedAccounts, onCreate, onClose])

    const handleModalClose = useCallback(() => {
        resetState()
        onClose()
    }, [onClose, resetState])

    const toggleAccount = useCallback((phone: string) => {
        setSelectedAccounts((prev) => (prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone]))
    }, [])

    return (
        <Modal open={open} onClose={handleModalClose}>
            <ModalDialog variant="outlined" sx={{ borderRadius: 'lg', minWidth: 550, maxWidth: 650, maxHeight: '90vh' }}>
                <ModalClose />
                <Typography level="h4" component="h2">
                    Add New Group
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                    Manually add a Telegram group or channel
                </Typography>
                <Divider sx={{ my: 2 }} />

                {modalMessage && (
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        {modalMessage}
                    </Alert>
                )}
                {modalError && (
                    <Alert
                        color="danger"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="danger" onClick={() => setModalError(null)}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {modalError}
                    </Alert>
                )}

                <Stack spacing={2} sx={{ overflow: 'auto' }}>
                    {/* Group Info */}
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Typography level="title-sm" sx={{ mb: 2 }}>
                            Group Information
                        </Typography>
                        <Stack spacing={2}>
                            <FormControl required>
                                <FormLabel>Peer ID</FormLabel>
                                <Input
                                    placeholder="Enter Telegram group/channel ID (e.g., -1001234567890)"
                                    value={peerId}
                                    onChange={(e) => setPeerId(e.target.value)}
                                    disabled={creating}
                                />
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                    The unique ID of the Telegram group or channel
                                </Typography>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Original Name</FormLabel>
                                <Input
                                    placeholder="Enter group name"
                                    value={originalName}
                                    onChange={(e) => setOriginalName(e.target.value)}
                                    disabled={creating}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Broker Company Name</FormLabel>
                                <Input
                                    placeholder="Enter broker company name"
                                    value={brokerCompanyName}
                                    onChange={(e) => setBrokerCompanyName(e.target.value)}
                                    disabled={creating}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Join Link</FormLabel>
                                <Input placeholder="https://t.me/..." value={joinLink} onChange={(e) => setJoinLink(e.target.value)} disabled={creating} />
                            </FormControl>
                        </Stack>
                    </Sheet>

                    {/* Select Accounts to Watch */}
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>
                            Select Accounts to Watch This Group
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                            Choose which accounts should monitor this group for loads
                        </Typography>
                        {activeAccounts.length === 0 ? (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                No active accounts available. Add and activate a Telegram account first.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {activeAccounts.map((account) => (
                                    <Sheet
                                        key={account.phone}
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 'sm',
                                            cursor: 'pointer',
                                            borderColor: selectedAccounts.includes(account.phone) ? 'primary.500' : undefined,
                                            bgcolor: selectedAccounts.includes(account.phone) ? 'primary.softBg' : undefined,
                                            '&:hover': { bgcolor: selectedAccounts.includes(account.phone) ? 'primary.softBg' : 'background.level1' },
                                        }}
                                        onClick={() => !creating && toggleAccount(account.phone)}
                                    >
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Switch
                                                    checked={selectedAccounts.includes(account.phone)}
                                                    disabled={creating}
                                                    onChange={() => toggleAccount(account.phone)}
                                                />
                                                <Typography level="body-sm">{account.phone}</Typography>
                                            </Stack>
                                            <Chip size="sm" variant="soft" color="success">
                                                active
                                            </Chip>
                                        </Stack>
                                    </Sheet>
                                ))}
                            </Stack>
                        )}
                    </Sheet>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="plain" color="neutral" onClick={handleModalClose} disabled={creating}>
                            Cancel
                        </Button>
                        <Button variant="solid" color="primary" onClick={handleCreate} loading={creating} disabled={!peerId.trim()}>
                            Create Group
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    )
}
